<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type {
    BetaProfileIntegration,
    BetaProfileIntegrationProvider,
} from '@/types/beta-dashboard'
import { normalizeBetaIntegrationUsername } from '@/utils/beta-integrations'

const props = defineProps<{
    open: boolean
    provider: BetaProfileIntegrationProvider
    profile?: BetaProfileIntegration | null
    searching?: boolean
    connecting?: boolean
    error?: string
}>()

const emit = defineEmits<{
    close: []
    search: [provider: BetaProfileIntegrationProvider, username: string]
    connect: [provider: BetaProfileIntegrationProvider, username: string]
    reset: []
}>()

const username = ref('')
const validationError = ref('')

watch(
    [() => props.open, () => props.provider],
    ([open]) => {
        if (!open) return
        username.value = ''
        validationError.value = ''
    },
)

const label = computed(() => props.provider === 'insta' ? 'Instagram' : 'YouTube')
const fieldLabel = computed(() => props.provider === 'insta' ? 'Nome do usuário' : 'URL do canal')
const placeholder = computed(() => props.provider === 'insta' ? '@seuusuario' : 'https://youtube.com/@seucanal')
const busy = computed(() => Boolean(props.searching || props.connecting))

const normalizedUsername = () => normalizeBetaIntegrationUsername(props.provider, username.value)

const search = () => {
    const normalized = normalizedUsername()
    if (!normalized) {
        validationError.value = `Informe ${props.provider === 'insta' ? 'o usuário' : 'o canal'}.`
        return
    }

    validationError.value = ''
    username.value = normalized
    emit('search', props.provider, normalized)
}

const reset = () => {
    validationError.value = ''
    emit('reset')
}
</script>

<template>
    <Teleport to="body">
        <div v-if="open" class="beta-profile-dialog" role="dialog" aria-modal="true">
            <button type="button" class="beta-profile-dialog__backdrop" aria-label="Fechar" @click="emit('close')"></button>

            <form class="beta-profile-dialog__panel" @submit.prevent="profile ? emit('connect', provider, normalizedUsername()) : search()">
                <header>
                    <div>
                        <p>Integrações</p>
                        <h2>Conectar {{ label }}</h2>
                    </div>
                    <button type="button" class="is-close" aria-label="Fechar" @click="emit('close')">×</button>
                </header>

                <div class="beta-profile-dialog__body">
                    <template v-if="!profile">
                        <p>Informe o perfil que possui os conteúdos relevantes para o seu site.</p>
                        <label>
                            <span>{{ fieldLabel }}</span>
                            <input v-model="username" type="text" :placeholder="placeholder" :disabled="busy">
                        </label>
                        <p v-if="validationError || error" class="beta-profile-dialog__error">
                            {{ validationError || error }}
                        </p>
                    </template>

                    <template v-else>
                        <div class="beta-profile-dialog__preview">
                            <img v-if="profile.picture" :src="profile.picture" :alt="profile.name || label">
                            <span v-else aria-hidden="true">{{ label.charAt(0) }}</span>
                            <div>
                                <small>Perfil encontrado</small>
                                <strong>{{ profile.name || username }}</strong>
                                <p>Confirme que este perfil pertence a você.</p>
                            </div>
                        </div>
                    </template>
                </div>

                <footer>
                    <button v-if="profile" type="button" class="is-cancel" :disabled="busy" @click="reset">Voltar</button>
                    <button v-else type="button" class="is-cancel" :disabled="busy" @click="emit('close')">Cancelar</button>
                    <button type="submit" class="is-save" :disabled="busy">
                        {{ searching ? 'Buscando…' : connecting ? 'Conectando…' : profile ? 'Sim, conectar' : 'Buscar perfil' }}
                    </button>
                </footer>
            </form>
        </div>
    </Teleport>
</template>

<style scoped>
.beta-profile-dialog {
    position: fixed;
    inset: 0;
    z-index: 110;
    display: grid;
    place-items: center;
    padding: 18px;
}

.beta-profile-dialog__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(20, 35, 57, 0.64);
    backdrop-filter: blur(7px);
}

.beta-profile-dialog__panel {
    position: relative;
    width: min(510px, 100%);
    overflow: hidden;
    border-radius: 22px;
    background: #fff;
}

.beta-profile-dialog__panel > header,
.beta-profile-dialog__panel > footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 18px;
}

.beta-profile-dialog__panel > header {
    border-bottom: 1px solid #e8edf3;
}

.beta-profile-dialog__panel > header p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.beta-profile-dialog__panel > header h2 {
    margin-top: 2px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 700;
}

.beta-profile-dialog__panel .is-close {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #f0f4f9;
    color: #60718a;
    font-size: 18px;
}

.beta-profile-dialog__body {
    display: grid;
    gap: 12px;
    padding: 17px 18px;
    color: #7c8ba0;
    font-size: 12px;
}

.beta-profile-dialog__body label {
    display: grid;
    gap: 6px;
}

.beta-profile-dialog__body label > span {
    color: #3a506d;
    font-size: 12px;
    font-weight: 700;
}

.beta-profile-dialog__body input {
    width: 100%;
    border: 1px solid #dce4ef;
    border-radius: 12px;
    padding: 10px 11px;
    color: #172d4a;
    font-size: 11px;
    outline: none;
}

.beta-profile-dialog__body input:focus {
    border-color: var(--main-color);
    outline: 3px solid color-mix(in srgb, var(--main-color) 9%, transparent);
    outline-offset: 1px;
}

.beta-profile-dialog__error {
    color: #b24c4c;
    font-weight: 700;
}

.beta-profile-dialog__preview {
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr);
    align-items: center;
    gap: 13px;
    border: 1px solid #e2e8f0;
    border-radius: 15px;
    background: #f8fafc;
    padding: 12px;
}

.beta-profile-dialog__preview > img,
.beta-profile-dialog__preview > span {
    display: grid;
    width: 64px;
    height: 64px;
    place-items: center;
    border-radius: 50%;
    background: color-mix(in srgb, var(--main-color) 10%, #e9eef5);
    object-fit: cover;
    color: var(--main-color);
    font-size: 18px;
    font-weight: 700;
}

.beta-profile-dialog__preview small,
.beta-profile-dialog__preview strong,
.beta-profile-dialog__preview p {
    display: block;
}

.beta-profile-dialog__preview small {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
}

.beta-profile-dialog__preview strong {
    margin-top: 3px;
    color: #2b425f;
    font-size: 12px;
}

.beta-profile-dialog__preview p {
    margin-top: 4px;
    font-size: 10px;
}

.beta-profile-dialog__panel > footer {
    justify-content: flex-end;
    border-top: 1px solid #e8edf3;
}

.beta-profile-dialog__panel > footer button {
    min-height: 36px;
    border-radius: var(--beta-button-radius);
    padding: 0 13px;
    font-size: 10px;
    font-weight: 700;
}

.beta-profile-dialog__panel .is-cancel {
    color: #74859b;
}

.beta-profile-dialog__panel .is-save {
    background: var(--main-color);
    color: #fff;
}

.beta-profile-dialog__panel > footer button:disabled {
    cursor: wait;
    opacity: 0.55;
}
</style>
